import { cn } from '@/lib/utils';
import auroraBg from '../assets/aurora.webp';

/**
 * The gradient that bleeds into the top of the sidebar.
 *
 * It stops at the sidebar: an earlier version continued across the page card
 * too, and the wash over the page read as a stain rather than an accent.
 *
 * Two fades are combined — down the page, and out to the right — because the
 * image is a single non-repeating blob, so without the second one its right
 * edge lands as a visible vertical seam.
 *
 * Decorative: no pointer events, and hidden from assistive tech.
 */
export function AuroraWash({
    className,
    /**
     * Framed for the collapsed rail rather than the full one.
     *
     * The framing below is sized as a PERCENTAGE of the element and offset in
     * pixels, which only works at one width. At 48px the image scaled to 72px
     * and the -56px offset pushed all but its pale left edge off screen, so
     * the wash simply vanished when the sidebar collapsed.
     */
    compact = false,
    /**
     * Degrees to turn the artwork's hue — each workspace has its own, so the
     * rail tells you where you are before you read anything. Animated, so
     * switching reads as the same place changing colour, not a page reload.
     */
    hue = 0,
}: {
    className?: string;
    compact?: boolean;
    hue?: number;
}) {
    // Full strength down to the halfway mark, then away sharply: the top half
    // carries the colour and the bottom half is almost entirely dissolve, so
    // the wash never reaches the nav with enough weight to tint it. Stepped
    // rather than linear — a constant rate reads as a band with an end to it,
    // whereas easing the alpha out leaves no edge to find.
    const fade = [
        [
            'linear-gradient(to bottom,',
            'rgb(0 0 0 / 1) 0%,',
            'rgb(0 0 0 / 1) 45%,',
            'rgb(0 0 0 / 0.72) 55%,',
            'rgb(0 0 0 / 0.48) 64%,',
            'rgb(0 0 0 / 0.3) 72%,',
            'rgb(0 0 0 / 0.18) 79%,',
            'rgb(0 0 0 / 0.1) 86%,',
            'rgb(0 0 0 / 0.04) 93%,',
            'rgb(0 0 0 / 0) 100%)',
        ].join(' '),
        'linear-gradient(to right, black 0%, black 70%, transparent 100%)',
    ].join(', ');
    return (
        <div
            aria-hidden
            className={cn(
                // 176px: the sidebar's 8px inset, the 64px header, the search
                // block and the group heading — ending on the Dashboard row.
                'pointer-events-none absolute top-0 h-44 bg-no-repeat opacity-55 transition-[background-position,background-size,filter] duration-500 ease-out dark:opacity-25',
                className,
            )}
            style={{
                backgroundImage: `url(${auroraBg})`,
                // Sized to the element, not in pixels: the blob's saturated
                // middle has to land inside the sidebar's width. Scaled down to
                // a fixed 1100px it did not — only the artwork's pale left edge
                // showed, and the whole thing read as a faint smudge.
                /*
                 * 384px is what `150%` comes to on the full 256px rail. Held
                 * at that absolute size when compact rather than recomputed
                 * from the narrower element, so the blob stays the same
                 * physical size and only the window over it narrows.
                 */
                backgroundSize: compact ? '384px auto' : '150% auto',
                /*
                 * Pulled left so more of the blob's body sits in view rather
                 * than its left edge, and up so the window lands on the middle
                 * of the artwork instead of its top.
                 *
                 * Compact keeps the same point of the artwork in the middle of
                 * a 48px window that the full rail centres in a 256px one.
                 */
                backgroundPosition: compact ? '-160px -280px' : '-56px -280px',
                filter: hue ? `hue-rotate(${hue}deg)` : undefined,
                maskImage: fade,
                WebkitMaskImage: fade,
                maskComposite: 'intersect',
                WebkitMaskComposite: 'source-in',
            }}
        />
    );
}
